import type { BookmakerSessionFlag } from '@/lib/bookmakerSessionFlag'
import type { NavGroup } from './nav'

export type StaffMeCaller = {
  staffUserId?: string
  grants?: string[]
  /** Lab Market access — owners / missing → treat as all. */
  bookmakerSessionFlag?: BookmakerSessionFlag
}

export function isStaffCaller(caller: StaffMeCaller | undefined): boolean {
  return Boolean(caller?.staffUserId)
}

export function hasAnyGrant(grants: string[] | undefined, codes: string[]): boolean {
  if (!codes.length) return true
  const set = new Set(grants ?? [])
  return codes.some((c) => set.has(c))
}

/**
 * Book / Premium BM dropdown children — page-scoped codes.
 * Keyed by nav item id so Book and Premium BM do not share the same grants.
 */
const WORKSPACE_CHILD_GRANTS: Record<string, Record<string, string[]>> = {
  'book-management': {
    'net-exposure': ['books.view', 'risk.view'],
    'bet-ticker': ['bet-ticker.view'],
    previous: ['bet-ticker-previous.view'],
    'previous-new': ['bet-ticker-previous.view'],
  },
  'premium-bm': {
    'net-exposure': ['books.premium.view'],
    'bet-ticker': ['premium-bet-ticker.view'],
    previous: ['premium-bet-ticker-previous.view'],
  },
}

/** Nav item id → any-of grants. Missing entry = always visible (owners/settings). */
const NAV_ITEM_GRANTS: Record<string, string[]> = {
  'user-access': ['staff.view', 'staff.write', 'staff.create'],
  'member-list': ['downline.view'],
  // Lab: Hierarchy Search is its own grant; downline.view alone is not enough.
  'hierarchy-search': ['hierarchy-search.view'],
  create: ['create-downline'],
  // Master Access Level has no Sign-in 2FA — do not OR downline.view / edit-downline.
  'player-2fa': ['2fa.write'],
  // Parent shows if any Book Management mode grant is present; children filtered below.
  'book-management': [
    'books.view',
    'risk.view',
    'bet-ticker.view',
    'bet-ticker-previous.view',
  ],
  'premium-bm': [
    'books.premium.view',
    'premium-bet-ticker.view',
    'premium-bet-ticker-previous.view',
  ],
  // Account Statement page-scoped (Master: View My P/L ≠ View Turnover ≠ View Player P/L).
  'my-pnl': ['my-pnl.view'],
  'pl-statement': ['player-pnl.view'],
  // Transfer Statement page — Give/Take (credit-transfer) is Member List only.
  transfers: ['transfer.view'],
  turnover: ['statements.view'],
  'commission-report': ['commission.view', 'commission.write'],
  activity: ['logs.view'],
  // All Logs — page-scoped. Do not OR logs.view onto sibling pages (one checkbox
  // must not unlock every Master/TM log). CL Transfers keeps legacy logs.view.
  'logs-cl-transfers': ['cl-transfers.view', 'logs.view'],
  'logs-multiple-device-ids': ['multiple-device-ids.list'],
  'logs-betfair-rates': ['betfair-rates-logs.view'],
  'logs-invalid-bets': ['invalid-bets.list', 'logs.invalid-bets'],
  'logs-blocked-device-ids': ['blocked-device-ids.add', 'blocked-device-ids.delete'],
  'logs-blocked-ip': ['blocked-ip.view', 'blocked-ip.add', 'blocked-ip.delete'],
  'logs-login-from-blocked-ip': [
    'login-from-blocked-ip.view',
    'login-from-blocked-ip.view-ip',
  ],
}

export function filterNavByGrants(
  groups: NavGroup[],
  caller: StaffMeCaller | undefined,
): NavGroup[] {
  if (!isStaffCaller(caller)) return groups
  const grants = caller?.grants
  return groups
    .map((g) => ({
      ...g,
      items: g.items.flatMap((item) => {
        const needed = NAV_ITEM_GRANTS[item.id]
        if (needed && !hasAnyGrant(grants, needed)) return []
        if (!item.children?.length) return [item]
        const children = item.children.filter((child) => {
          const childNeeded = WORKSPACE_CHILD_GRANTS[item.id]?.[child.id]
          if (!childNeeded) return true
          return hasAnyGrant(grants, childNeeded)
        })
        if (!children.length) return []
        const landing = children[0]!
        return [
          {
            ...item,
            to: landing.to,
            children,
            description: children.map((c) => c.label).join(', '),
          },
        ]
      }),
    }))
    .filter((g) => g.items.length > 0)
}

/** First rail path staff may open (post-login falldown). Pass the built NAV rail. */
export function firstAccessiblePath(
  caller: StaffMeCaller | undefined,
  groups: NavGroup[],
  fallback = '/overview',
): string {
  if (!isStaffCaller(caller)) return fallback
  for (const group of filterNavByGrants(groups, caller)) {
    for (const item of group.items) {
      if (item.to && canAccessUiPath(caller, item.to)) return item.to
    }
  }
  return fallback
}

export function canAccessUiPath(caller: StaffMeCaller | undefined, pathname: string): boolean {
  if (!isStaffCaller(caller)) return true
  // User Access Level: list vs create are separate (Agent-API staff.create / staff.write).
  if (pathname === '/users/new' || pathname.startsWith('/users/new/')) {
    return hasAnyGrant(caller?.grants, ['staff.create'])
  }
  if (pathname.startsWith('/users')) {
    return hasAnyGrant(caller?.grants, ['staff.view', 'staff.write', 'staff.create'])
  }
  if (pathname.startsWith('/hierarchy-search')) {
    return hasAnyGrant(caller?.grants, ['hierarchy-search.view'])
  }
  if (pathname.startsWith('/create')) return hasAnyGrant(caller?.grants, ['create-downline'])
  if (pathname.startsWith('/player-2fa')) {
    return hasAnyGrant(caller?.grants, ['2fa.write'])
  }
  // Member list / detail browse; Edit tab gated separately via canEditDownline.
  if (pathname.startsWith('/downline') || pathname.startsWith('/member')) {
    return hasAnyGrant(caller?.grants, ['downline.view', 'edit-downline', 'credit-transfer'])
  }
  // Book Management — page-scoped (books.view must not unlock Bet Ticker).
  if (pathname.startsWith('/book-management/bet-ticker') || pathname === '/bet-ticker') {
    return hasAnyGrant(caller?.grants, ['bet-ticker.view'])
  }
  if (pathname.startsWith('/book-management/previous')) {
    return hasAnyGrant(caller?.grants, ['bet-ticker-previous.view'])
  }
  if (
    pathname.startsWith('/book-management/net-exposure') ||
    pathname.startsWith('/book-management') ||
    pathname.startsWith('/net-exposure')
  ) {
    return hasAnyGrant(caller?.grants, ['books.view', 'risk.view'])
  }
  // Premium BM Management — distinct grants from Book Management.
  if (pathname.startsWith('/premium-bm/bet-ticker')) {
    return hasAnyGrant(caller?.grants, ['premium-bet-ticker.view'])
  }
  if (pathname.startsWith('/premium-bm/previous')) {
    return hasAnyGrant(caller?.grants, ['premium-bet-ticker-previous.view'])
  }
  if (pathname.startsWith('/premium-bm')) {
    return hasAnyGrant(caller?.grants, ['books.premium.view'])
  }
  if (pathname.startsWith('/settings/security')) {
    return hasAnyGrant(caller?.grants, [
      'google.authentication.enable.two.factor.authentication',
      'google.authentication.deactivate.yes.no.confirm.deactivate',
    ])
  }
  if (pathname.startsWith('/reports/transfers')) {
    return hasAnyGrant(caller?.grants, ['transfer.view'])
  }
  // PL Statement / My P/L page-scoped (Turn Over must not unlock them).
  if (pathname.startsWith('/reports/pl-statement') || pathname.startsWith('/PLStatement')) {
    return hasAnyGrant(caller?.grants, ['player-pnl.view'])
  }
  if (pathname.startsWith('/reports/my-pnl')) {
    return hasAnyGrant(caller?.grants, ['my-pnl.view'])
  }
  if (pathname.startsWith('/reports/turnover')) {
    return hasAnyGrant(caller?.grants, ['statements.view'])
  }
  if (pathname.startsWith('/reports/commission')) {
    return hasAnyGrant(caller?.grants, ['commission.view', 'commission.write'])
  }
  // Unknown /reports/* — do not open via transfer.view alone (e.g. removed pg-deposits).
  if (pathname.startsWith('/reports') || pathname.startsWith('/statements')) {
    return hasAnyGrant(caller?.grants, ['statements.view', 'commission.view', 'commission.write'])
  }
  if (pathname.startsWith('/transfer')) return hasAnyGrant(caller?.grants, ['transfer.view'])
  if (pathname.startsWith('/logs/cl-transfers')) {
    return hasAnyGrant(caller?.grants, ['cl-transfers.view', 'logs.view'])
  }
  if (pathname.startsWith('/logs/multiple-device-ids')) {
    return hasAnyGrant(caller?.grants, ['multiple-device-ids.list'])
  }
  if (pathname.startsWith('/logs/betfair-rates')) {
    return hasAnyGrant(caller?.grants, ['betfair-rates-logs.view'])
  }
  if (pathname.startsWith('/logs/invalid-bets')) {
    return hasAnyGrant(caller?.grants, ['invalid-bets.list', 'logs.invalid-bets'])
  }
  if (pathname.startsWith('/logs/blocked-device-ids')) {
    return hasAnyGrant(caller?.grants, ['blocked-device-ids.add', 'blocked-device-ids.delete'])
  }
  if (pathname.startsWith('/logs/blocked-ip')) {
    return hasAnyGrant(caller?.grants, ['blocked-ip.view', 'blocked-ip.add', 'blocked-ip.delete'])
  }
  if (pathname.startsWith('/logs/login-from-blocked-ip')) {
    return hasAnyGrant(caller?.grants, [
      'login-from-blocked-ip.view',
      'login-from-blocked-ip.view-ip',
    ])
  }
  if (pathname.startsWith('/activity')) {
    return hasAnyGrant(caller?.grants, ['logs.view'])
  }
  // Unknown /logs/* — do not open via legacy logs.view alone.
  if (pathname.startsWith('/logs')) {
    return false
  }
  if (pathname.startsWith('/surveillance')) {
    return hasAnyGrant(caller?.grants, [
      'surveillance-beta.view',
      'surveillance-new.view',
      'surveillance.view',
    ])
  }
  if (pathname.startsWith('/pt')) return hasAnyGrant(caller?.grants, ['pt.view', 'pt.write'])
  return true
}

/** Event Lock/Unlock (Events column) — not the Member List Lock modal. */
export function canProductLock(caller: StaffMeCaller | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['product-lock'])
}

/** Member Lock/Unlock grant alone (roleslist label). */
export function canMemberLock(caller: StaffMeCaller | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['lock'])
}

/** Member List Lock modal — Agent-API game-lock / game-status (Member Lock only). */
export function canWriteGameLock(caller: StaffMeCaller | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['lock'])
}

/** Member List Global Lock toolbar / column — Access Level Global Lock. */
export function canGlobalLock(caller: StaffMeCaller | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['global-lock'])
}

/** Direct-child Edit / Edit tab — staff need edit-downline (owners bypass). */
export function canEditDownline(caller: StaffMeCaller | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['edit-downline'])
}

/** Member detail Balance + Login History — staff need downline.view (owners bypass). */
export function canViewDownline(caller: StaffMeCaller | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['downline.view'])
}

/** Member detail Bet List — Book Management → Bet Ticker → list bets. */
export function canViewBetList(caller: StaffMeCaller | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['bet-ticker.view'])
}

/** Member detail Net Exposure tab — Book Management → Net Exposure → view Net Exposure. */
export function canViewMemberNetExposure(caller: StaffMeCaller | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['books.view'])
}

/** Member detail Account Statement + Activity — Account Statement → Player P/L → View Player P/L. */
export function canViewPlayerPnl(caller: StaffMeCaller | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['player-pnl.view'])
}

/** Member detail Transfer Statements — Account Statement → Transfer Statement → View Transfer Statement. */
export function canViewTransferStatement(caller: StaffMeCaller | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['transfer.view'])
}

/** Member detail Commission — Account Statement → Commission → View Commission. */
export function canViewCommission(caller: StaffMeCaller | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['commission.view', 'commission.write'])
}

/**
 * Member detail tab visibility for Access Level staff.
 * Edit → edit-downline; Balance / Login History → downline.view;
 * Bet List → bet-ticker.view; Net Exposure → books.view;
 * Account Statement / Activity → player-pnl.view;
 * Transfer Statements → transfer.view; Commission → commission.view|write;
 * other tabs stay open.
 */
export function staffCanSeeMemberDetailTab(
  tabId: string,
  caller: StaffMeCaller | undefined,
): boolean {
  if (!isStaffCaller(caller)) return true
  if (tabId === 'edit') return canEditDownline(caller)
  if (tabId === 'balance' || tabId === 'login-history') return canViewDownline(caller)
  if (tabId === 'bets') return canViewBetList(caller)
  if (tabId === 'net-exposure') return canViewMemberNetExposure(caller)
  if (tabId === 'statement' || tabId === 'activity') return canViewPlayerPnl(caller)
  if (tabId === 'transfers') return canViewTransferStatement(caller)
  if (tabId === 'commission') return canViewCommission(caller)
  return true
}

export function canCreateDownline(caller: StaffMeCaller | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['create-downline'])
}

/** User Access Level → Create user (add User Access Level). */
export function canCreateStaffUser(caller: StaffMeCaller | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['staff.create'])
}

/** User Access Level → Edit / Save staff (edit User Access Level). */
export function canEditStaffUser(caller: StaffMeCaller | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['staff.write'])
}

export function canCreditTransfer(caller: StaffMeCaller | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['credit-transfer'])
}

export function canBulkTransfer(caller: StaffMeCaller | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['bulk-transfer'])
}

/** Account menu → Change password (Master Access Level Change Password). */
export function canChangePassword(caller: StaffMeCaller | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  // Lab long codes still appear on pre-migration allow_access rows.
  return hasAnyGrant(caller?.grants, [
    'change-password.view',
    'change.password.change.password',
    'change.password.save.cancel',
  ])
}

/** Account menu → Two-factor / Google Authentication Access Level. */
export function canManageGoogleAuth(caller: StaffMeCaller | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, [
    'google.authentication.enable.two.factor.authentication',
    'google.authentication.deactivate.yes.no.confirm.deactivate',
  ])
}

/** SurveillanceBeta / PL View Graph — Generate Graph. Owners bypass. */
export function canGenerateSurveillanceBetaGraph(
  caller: StaffMeCaller | undefined,
): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['surveillance-beta.generate-graph'])
}
